import { forwardRef, type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'violet';
type Size = 'sm' | 'md';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

/**
 * Bewusst mehrere kräftige, unterscheidbare Farbvarianten statt nur
 * "primary/secondary" - Aktionen sollen auf einen Blick auseinanderzuhalten
 * sein (z.B. Excel-Export grün, OCR-Erfassung violett als eigene Funktion,
 * Löschen rot), statt dass alles dieselbe gedämpfte Akzentfarbe trägt.
 */
// bg/hover-Paare bewusst auf den jeweils "600/700"-Schritt statt "500/400" gelegt -
// weißer Text auf den helleren *-500-Tönen fällt unter den WCAG-AA-Grenzwert (4.5:1).
const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent-600 text-white hover:bg-accent-700 focus-visible:outline-accent-400 font-semibold',
  secondary: 'bg-surface-700 text-ivory-100 hover:bg-surface-600 focus-visible:outline-surface-400 border border-surface-600',
  ghost: 'bg-transparent text-ivory-300 hover:bg-surface-800 hover:text-ivory-100 focus-visible:outline-surface-400',
  danger: 'bg-danger-600 text-white hover:bg-danger-700 focus-visible:outline-danger-400 font-semibold',
  success: 'bg-success-700 text-white hover:bg-success-800 focus-visible:outline-success-400 font-semibold',
  violet: 'bg-violet-600 text-white hover:bg-violet-700 focus-visible:outline-violet-400 font-semibold',
};

const sizeClasses: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm rounded-md',
  md: 'px-4 py-2.5 text-sm rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 transition-all duration-150 outline-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      {children}
    </button>
  );
});
